import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { t } from '@sm/intl';
import { Box } from '@wds/box';
import { Input } from '@wds/input';
import { SelectMenu } from '@wds/select-menu';
import MultiSelect from './MultiSelect';
import OpenEndedCodingContext from '../../../OpenEndedCodingContext';
import { useFiltersActions, useStimuli, useFilters, useGeneral } from '../../../Context';
import { SORT_BY, FILTER_BY_TAGGED_STATUS } from '../../../types';
export const COPY = {
  filters: {
    id: 'OpenEndedCoding.Filters.filters',
    defaultMessage: 'Filter by',
    description: '[Type: Header][Vis: med] - Filter by label OEC'
  },
  searchPlaceholder: {
    id: 'OpenEndedCoding.Filters.searchPlaceholder',
    defaultMessage: 'Search for a keyword or theme',
    description: '[Type: Header][Vis: med] - Filters search placeholder OEC'
  },
  sortBy: {
    id: 'OpenEndedCoding.Filters.sortBy',
    defaultMessage: 'Sort by',
    description: '[Type: Header][Vis: med] - Sort by label OEC'
  },
  mostRecent: {
    id: 'OpenEndedCoding.Filters.mostRecent',
    defaultMessage: 'Sort: Most recent',
    description: '[Type: Header][Vis: med] - Sort by most recent OEC'
  },
  alphabetically: {
    id: 'OpenEndedCoding.Filters.alphabetically',
    defaultMessage: 'Sort: A to Z',
    description: '[Type: Header][Vis: med] - Sort by A - Z OEC'
  },
  themes: {
    id: 'OpenEndedCoding.Filters.themes',
    defaultMessage: 'Themes',
    description: '[Type: Header][Vis: med] - themes filter OEC'
  },
  stimuli: {
    id: 'OpenEndedCoding.Filters.stimuli',
    defaultMessage: 'Stimuli',
    description: '[Type: Header][Vis: med] - stimuli filter OEC'
  },
  responses: {
    id: 'OpenEndedCoding.Filters.responses',
    defaultMessage: 'Responses',
    description: '[Type: Header][Vis: med] - Responses label OEC'
  },
  allResponses: {
    id: 'OpenEndedCoding.Filters.allResponses',
    defaultMessage: 'All responses',
    description: '[Type: Header][Vis: med] - All responses option for filtering'
  },
  taggedResponses: {
    id: 'OpenEndedCoding.Filters.taggedResponses',
    defaultMessage: 'Tagged responses',
    description: '[Type: Header][Vis: med] - Tagged responses option for filtering '
  },
  untaggedResponses: {
    id: 'OpenEndedCoding.Filters.untaggedResponses',
    defaultMessage: 'Untagged responses',
    description: '[Type: Header][Vis: med] - Untagged responses option for filtering '
  }
};
const useStyles = createUseStyles(theme => ({
  form: {
    marginY: theme.spacing[4],
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end'
  },
  selectMenu: {
    // need a narrower select menu trigger
    '& span[class^="wrenchMenuContainer"] > div[class^="wrenchSelectMenu"]': {
      padding: '0px 42px 0px 12px'
    }
  }
}));
const Filters = () => {
  const classes = useStyles();
  // the search value used when fetching responses
  const {
    filterResponses
  } = useContext(OpenEndedCodingContext);
  const {
    stimuli
  } = useStimuli();
  const {
    isSolutions
  } = useGeneral();
  const {
    searchInput,
    sortValue,
    filterByTaggedStatus,
    selectedStimuli
  } = useFilters();
  const {
    setMultiple: setMultipleFilterFields
  } = useFiltersActions();
  const [userAction, setUserAction] = useState(false);
  const executeFilter = () => {
    filterResponses(Object.assign({
      search: searchInput,
      sortBy: sortValue,
      filterByTaggedStatus: filterByTaggedStatus
    }, isSolutions && {
      stimulusIds: selectedStimuli
    }));
  };
  // execute the filter on user actions
  useEffect(() => {
    if (userAction) {
      filterResponses(Object.assign({
        search: searchInput,
        sortBy: sortValue,
        filterByTaggedStatus: filterByTaggedStatus
      }, isSolutions && {
        stimulusIds: selectedStimuli
      }));
      setUserAction(false);
    }
  }, [userAction]);
  // handle sorting
  const handleSortByChange = e => {
    const value = e.currentTarget.getAttribute('data-value');
    if (value) {
      setMultipleFilterFields({
        sortValue: value
      });
    }
    setUserAction(true);
  };
  // handle filter by response type
  const handleFilterByResponseType = e => {
    const value = e.currentTarget.getAttribute('data-value');
    if (value) {
      setMultipleFilterFields({
        filterByTaggedStatus: value
      });
    }
    setUserAction(true);
  };
  // set the search input value
  const handleSearchChange = e => {
    setMultipleFilterFields({
      searchInput: e.currentTarget.value
    });
  };
  // handle enter search input
  const handleSearchOnKeyDown = e => {
    if (e.key === 'Enter') {
      executeFilter();
    }
  };
  // stimulus filter
  const handleStimulusSelect = id => {
    // this acts as a toggle
    // remove the stimulus if it already exists, include it if it doesn't exist
    setMultipleFilterFields({
      filterDisabled: false,
      selectedStimuli: selectedStimuli.includes(id) ? selectedStimuli.filter(sid => sid !== id) : [...selectedStimuli, id]
    });
  };
  return _jsxs("form", {
    className: classes.form,
    "aria-controls": "responsesList",
    onSubmit: e => e.preventDefault(),
    children: [_jsx(Box, {
      className: classes.selectMenu,
      mr: 2,
      children: _jsxs(SelectMenu, {
        "data-testid": "Filters__Responses",
        ariaLabel: t(COPY.responses),
        value: filterByTaggedStatus,
        name: t(COPY.responses),
        onChange: handleFilterByResponseType,
        children: [_jsx(SelectMenu.Option, {
          value: FILTER_BY_TAGGED_STATUS.ALL,
          children: t(COPY.allResponses)
        }), _jsx(SelectMenu.Option, {
          value: FILTER_BY_TAGGED_STATUS.TAGGED,
          children: t(COPY.taggedResponses)
        }), _jsx(SelectMenu.Option, {
          value: FILTER_BY_TAGGED_STATUS.UNTAGGED,
          children: _jsx(Box, {
            mr: 3,
            children: t(COPY.untaggedResponses)
          })
        })]
      })
    }), _jsx(Box, {
      className: classes.selectMenu,
      mr: 2,
      children: _jsxs(SelectMenu, {
        "data-testid": "Filters__SortBy",
        ariaLabel: t(COPY.sortBy),
        value: sortValue,
        name: t(COPY.sortBy),
        onChange: handleSortByChange,
        children: [_jsx(SelectMenu.Option, {
          value: SORT_BY.DATE,
          children: t(COPY.mostRecent)
        }), _jsx(SelectMenu.Option, {
          value: SORT_BY.ALPHABETICALLY,
          children: t(COPY.alphabetically)
        })]
      })
    }), isSolutions && _jsx(Box, {
      mr: 2,
      children: _jsx(MultiSelect, {
        title: t(COPY.stimuli),
        values: stimuli.map(({
          id,
          name
        }) => {
          return {
            id,
            label: name,
            selected: selectedStimuli.includes(id)
          };
        }),
        label: t(COPY.filters),
        handleSelect: handleStimulusSelect
      })
    }), _jsx(Box, {
      flexItemGrow: true,
      mr: 2,
      children: _jsx(Input, {
        placeholder: t(COPY.searchPlaceholder),
        ariaLabel: t(COPY.filters),
        stretched: true,
        value: searchInput,
        onChange: handleSearchChange,
        onKeyDown: handleSearchOnKeyDown
      })
    })]
  });
};
export default Filters;